import {
  Button,
  PageContainer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@ccp/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AppErrorState } from '../../app/error-state.js';
import {
  type TicketStatus,
  useAssignTicket,
  useChangeTicketStatus,
  useTicket,
} from '../../features/tickets/api.js';
import { TicketDetailView } from '../../features/tickets/components/ticket-detail.js';
import { useAgentSession } from '../../session/session-provider.js';
import { usePermission } from '../../session/use-permission.js';

export const Route = createFileRoute('/tickets/$ticketId')({ component: TicketScreen });

const statuses = ['open', 'pending', 'resolved', 'closed'] as const;

/**
 * One ticket, and the two things an agent does to one that are not saying something on a channel:
 * move it, and own it.
 *
 * The status list is the domain's four and the **API refuses an illegal move** — `open → pending →
 * resolved → closed` and the ways back, with a reopen allowed from both endings. Offering only the
 * legal ones here would be a second copy of that table in a place nobody would think to update;
 * what this does instead is show them all and report what the platform said, which is also the
 * only version that stays right when the table changes.
 */
function TicketScreen() {
  const { t } = useTranslation('tickets');
  const { ticketId } = Route.useParams();
  const detail = useTicket(ticketId);
  const mayWrite = usePermission('tickets.write');
  const changeStatus = useChangeTicketStatus();
  const assign = useAssignTicket();
  const myAgentId = useAgentSession((snapshot) => snapshot.context.agentId);

  if (detail.isError) {
    return (
      <AppErrorState
        error={new Error(t('detail.unavailable'))}
        resetErrorBoundary={() => detail.refetch()}
      />
    );
  }
  if (detail.data === undefined) {
    return (
      <PageContainer width="medium">
        <div role="status" aria-busy="true" aria-label={t('detail.loading')}>
          <Skeleton className="h-40 w-full" />
        </div>
      </PageContainer>
    );
  }

  const ticket = detail.data.ticket;
  const mine = ticket.assigneeId !== undefined && ticket.assigneeId === myAgentId;

  return (
    <PageContainer width="medium">
      <TicketDetailView
        detail={detail.data}
        actions={
          mayWrite ? (
            <div className="flex items-center gap-(--space-xs)">
              <Select
                items={statuses.map((status) => ({ value: status, label: t(`status.${status}`) }))}
                value={ticket.status}
                disabled={changeStatus.isPending}
                onValueChange={(next) => {
                  const chosen = String(next ?? '');
                  const status = statuses.find((option) => option === chosen);
                  if (status === undefined || status === ticket.status) {
                    return;
                  }
                  changeStatus.mutate(
                    { ticketId, status: status as TicketStatus },
                    {
                      onSuccess: () => toast.success(t('detail.statusChanged')),
                      // The domain refuses an illegal move with a sentence; this is where an agent
                      // reads it, rather than a list that quietly offered fewer options.
                      onError: () => toast.error(t('detail.statusRefused')),
                    },
                  );
                }}
              >
                <SelectTrigger aria-label={t('detail.changeStatus')} className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {t(`status.${status}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={mine ? 'ghost' : 'secondary'}
                disabled={assign.isPending || myAgentId === undefined}
                onClick={() =>
                  assign.mutate(
                    // Handing it back is as real as taking it: an agent going off shift with three
                    // tickets open should be able to put them down.
                    { ticketId, assigneeId: mine ? null : (myAgentId as string) },
                    { onError: () => toast.error(t('detail.assignFailed')) },
                  )
                }
              >
                {mine ? t('detail.handBack') : t('detail.takeIt')}
              </Button>
            </div>
          ) : undefined
        }
      />
    </PageContainer>
  );
}
